import { tf, tr } from "../i18n";
import { audioContext } from "./sound";
import { loadNotificationSettings, type SoundId } from "./settings";

export const MAX_RING_MS = 60_000;

let ringing = false;
let hardStop: ReturnType<typeof setTimeout> | null = null;
let activeSource: AudioBufferSourceNode | null = null;
const ringBuffers = new Map<SoundId, Promise<AudioBuffer>>();
const RING_ASSET_VERSION = "20260915-aosp1";
let lastDiag = "";
const diagListeners = new Set<(diagnostic: string) => void>();

function setDiag(value: string): void {
  lastDiag = value;
  diagListeners.forEach((listener) => listener(value));
}

/** Subscribe the settings panel to ringtone diagnostics. */
export function onRingDiag(listener: (diagnostic: string) => void): () => void {
  diagListeners.add(listener);
  listener(lastDiag);
  return () => diagListeners.delete(listener);
}

function loadRingBuffer(ac: AudioContext, id: SoundId): Promise<AudioBuffer> {
  let pending = ringBuffers.get(id);
  if (!pending) {
    pending = fetch(`/sounds/call-${id}.mp3?v=${RING_ASSET_VERSION}`, { cache: "no-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`ring asset HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((bytes) => ac.decodeAudioData(bytes));
    ringBuffers.set(id, pending);
  }
  return pending;
}

function playBuffer(ac: AudioContext, buffer: AudioBuffer, loop: boolean): AudioBufferSourceNode {
  const source = ac.createBufferSource();
  source.buffer = buffer;
  source.loop = loop;
  source.connect(ac.destination);
  source.start();
  return source;
}

/** Settings preview uses the same real ringtone asset as an incoming call. */
export function previewCallSound(id: SoundId): void {
  const ac = audioContext();
  if (!ac) return;
  const play = async () => {
    try {
      const source = playBuffer(ac, await loadRingBuffer(ac, id), false);
      source.stop(ac.currentTime + Math.min(5, source.buffer?.duration ?? 5));
    } catch {
      // Audio preview is best effort and never changes call state.
    }
  };
  if (ac.state === "suspended") void ac.resume().then(play).catch(() => {});
  else void play();
}

/** Start an idempotent ringtone and stop it unconditionally after 60 seconds. */
export function startRinging(): void {
  if (ringing) return;
  ringing = true;
  setDiag(tr("call.ringDiagStarting"));
  hardStop = setTimeout(stopRinging, MAX_RING_MS);

  const ac = audioContext();
  if (!ac) {
    setDiag(tf("call.ringBlocked", { prefix: "audio-context" }));
    return;
  }
  const begin = async () => {
    try {
      const id = loadNotificationSettings().callTone;
      const buffer = await loadRingBuffer(ac, id);
      if (!ringing || activeSource) return;
      activeSource = playBuffer(ac, buffer, true);
      setDiag(tf("call.ringStarted", { prefix: id }));
    } catch (error) {
      if (ringing) setDiag(tf("call.ringBlocked", { prefix: String(error) }));
    }
  };
  if (ac.state === "suspended") {
    void ac.resume().then(() => begin()).catch(() => {
      if (ringing) setDiag(tf("call.ringBlocked", { prefix: "autoplay" }));
    });
  } else {
    void begin();
  }
}

/** Stop timers and any phrase currently sounding. Safe to call repeatedly. */
export function stopRinging(): void {
  ringing = false;
  if (hardStop) clearTimeout(hardStop);
  hardStop = null;
  if (activeSource) {
    try {
      activeSource.stop();
    } catch {
      // The source may already have stopped.
    }
    activeSource.disconnect();
  }
  activeSource = null;
}

/* ------------------------------------------------------------------ */
/* Гудок исходящего вызова (Антон, 2026-09-17 14:40: «при звонке кому-то
 * нет никакого гудка, непонятно идёт вызов или нет»).
 *
 * Синтезируем стандартный европейский ringback: 425 Гц, 1 с звук / 4 с
 * пауза — без отдельного аудиофайла, через тот же AudioContext. Играет
 * ровно пока я один в звонке после того, как позвонил (soloSecondsLeft
 * в rtc.ts), гаснет при ответе, отказе или выходе. Тихий (gain 0.12):
 * это фон ожидания, а не рингтон.                                     */

let dialOsc: OscillatorNode | null = null;
let dialGain: GainNode | null = null;
let dialTimer: ReturnType<typeof setInterval> | null = null;
/**
 * ЭПОХА ВЛАДЕНИЯ ГУДКОМ (замер 2026-09-21, дефект F).
 *
 * Состояние гудка глобальное, а start на УСЫПЛЁННОМ контексте запускает
 * осциллятор не сразу, а в .then() после resume(). Замер показал дыру:
 * пришедший в это окно stopDialTone глушить ещё нечего — осциллятор
 * стартует ПОСЛЕ него и висит навсегда (гудок не гаснет после ответа
 * собеседника). Лечится счётчиком поколений: start берёт себе номер,
 * отложенный запуск проверяет, что он всё ещё актуален, а stop двигает
 * номер и тем самым отменяет ещё не состоявшийся запуск.
 *
 * Порядок «cleanup умирающего экземпляра / эффект живого» при этом НЕ
 * проблема: замер на настоящем React 19 (StrictMode и перепривязка
 * держателя по key комнаты) даёт stop -> start, то есть гудок
 * перезапускается корректно. Ownership-протокол здесь не нужен.
 */
let dialEpoch = 0;

export function startDialTone(): void {
  if (dialOsc) return;
  const ac = audioContext();
  if (!ac) {
    setDiag(tf("call.ringBlocked", { prefix: "dialtone/audio-context" }));
    return;
  }
  const epoch = ++dialEpoch;
  const begin = () => {
    // Нас отменили, пока мы ждали resume() (дефект F), или гудок уже
    // запущен другим экземпляром эффекта — второй осциллятор не нужен.
    if (epoch !== dialEpoch || dialOsc) return;
    const gain = ac.createGain();
    gain.gain.value = 0;
    gain.connect(ac.destination);
    const osc = ac.createOscillator();
    osc.type = "sine";
    osc.frequency.value = 425;
    osc.connect(gain);
    osc.start();
    dialOsc = osc;
    dialGain = gain;
    const burst = () => {
      if (!dialGain) return;
      const t = ac.currentTime;
      dialGain.gain.cancelScheduledValues(t);
      dialGain.gain.setValueAtTime(0, t);
      dialGain.gain.linearRampToValueAtTime(0.12, t + 0.02);
      dialGain.gain.setValueAtTime(0.12, t + 0.98);
      dialGain.gain.linearRampToValueAtTime(0, t + 1.0);
    };
    burst();
    dialTimer = setInterval(burst, 5000);
  };
  if (ac.state === "suspended") {
    /* ПОЧЕМУ ЗДЕСЬ ДИАГНОСТИКА, А НЕ ПУСТОЙ catch (замер 2026-09-21,
       дефект C): гудок запускается из эффекта ПОСЛЕ сетевого join(),
       то есть далеко от жеста человека. Если контекст к этому моменту
       усыплён (вкладка/WebView уходили в фон, а разблокировщик из
       sound.ts срабатывает только один раз), resume() отклоняется по
       autoplay-политике — и раньше это молча уходило в пустой catch:
       гудка нет, в логе ни строчки, причина неотличима от «код не
       вызвался». Теперь состояние видно там же, где диагностика
       рингтона. */
    void ac
      .resume()
      .then(() => {
        if (epoch !== dialEpoch) return;
        begin();
      })
      .catch(() => {
        if (epoch === dialEpoch) setDiag(tf("call.ringBlocked", { prefix: "dialtone/autoplay" }));
      });
  } else {
    begin();
  }
}

export function stopDialTone(): void {
  // Двигаем эпоху ВСЕГДА: это отменяет отложенный запуск, который ещё
  // ждёт resume() и иначе включил бы гудок уже после остановки (F).
  dialEpoch++;
  if (dialTimer) {
    clearInterval(dialTimer);
    dialTimer = null;
  }
  try {
    dialOsc?.stop();
  } catch {
    /* already stopped */
  }
  dialOsc?.disconnect();
  dialGain?.disconnect();
  dialOsc = null;
  dialGain = null;
}
